import React from 'react';
import { TestTube, CheckCircle, AlertCircle } from 'lucide-react';
import type { FirebaseConfig, TestResults } from '../types';

interface FirebaseSettingsTabProps {
  config: FirebaseConfig;
  onChange: (config: FirebaseConfig) => void;
  testResults: TestResults;
  onTest: (service: string) => void;
}

const FirebaseSettingsTab: React.FC<FirebaseSettingsTabProps> = ({
  config,
  onChange,
  testResults,
  onTest
}) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Configuración Firebase</h3>
          <p className="text-sm text-gray-600">Para notificaciones push y funciones en la nube</p>
        </div>
        <div className="flex items-center space-x-3">
          <label className="flex items-center space-x-2 cursor-pointer">
            <span className="text-sm font-medium text-gray-700">
              {config.enabled ? 'Habilitado' : 'Deshabilitado'}
            </span>
            <button
              type="button"
              onClick={() => onChange({ ...config, enabled: !config.enabled })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                config.enabled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config.enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </label>
          {testResults.firebase !== undefined && (
            <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${
              testResults.firebase ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {testResults.firebase ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              <span className="text-sm">{testResults.firebase ? 'Conectado' : 'Error'}</span>
            </div>
          )}
          <button
            onClick={() => onTest('firebase')}
            disabled={!config.enabled}
            className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <TestTube className="w-4 h-4" />
            <span>Probar</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">API Key *</label>
          <input
            type="text"
            value={config.apiKey}
            onChange={(e) => onChange({ ...config, apiKey: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="AIza..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Auth Domain</label>
          <input
            type="text"
            value={config.authDomain}
            onChange={(e) => onChange({ ...config, authDomain: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="proyecto.firebaseapp.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Project ID *</label>
          <input
            type="text"
            value={config.projectId}
            onChange={(e) => onChange({ ...config, projectId: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="mi-proyecto-id"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Storage Bucket</label>
          <input
            type="text"
            value={config.storageBucket}
            onChange={(e) => onChange({ ...config, storageBucket: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="proyecto.appspot.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Messaging Sender ID</label>
          <input
            type="text"
            value={config.messagingSenderId}
            onChange={(e) => onChange({ ...config, messagingSenderId: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="123456789"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">App ID</label>
          <input
            type="text"
            value={config.appId}
            onChange={(e) => onChange({ ...config, appId: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="1:123:web:abc"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            VAPID Key (para notificaciones push)
          </label>
          <input
            type="text"
            value={config.vapidKey}
            onChange={(e) => onChange({ ...config, vapidKey: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="BG7..."
          />
        </div>
      </div>
    </div>
  );
};

export default FirebaseSettingsTab;
